import { useEffect, useMemo, useRef } from 'react';
import { captureAppError } from '@leagueplatform/observability';
import { SENTRY_CONSTANTS } from 'common/constants';
import { useConfigProperty } from '@leagueplatform/core';
import { useAnalyticsIdentify } from '@leagueplatform/web-common';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { BasePage } from 'components/base-page/base-page.component';
import { useOnboarding } from 'hooks/use-onboarding/use-onboarding.hook';

export const PrivateRoutesContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isOnboardingComplete, currentStep, isLoading, userProfileError } =
    useOnboarding();
  const reportedProfileError = useRef<unknown>();

  useEffect(() => {
    if (!userProfileError) {
      reportedProfileError.current = undefined;
      return;
    }
    if (reportedProfileError.current === userProfileError) return;
    reportedProfileError.current = userProfileError;

    // Socket errors can contain the full response; report only safe flow context.
    captureAppError(new Error('Post-login user profile unavailable'), {
      errorName: 'Post-login user profile unavailable',
      ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
      tags: {
        operation: 'get_user_profile',
        stage: 'post_login_onboarding',
        blocking: false,
      },
      context: {
        profileFallback: {
          component: 'PrivateRoutesContainer',
          isOnboardingComplete,
          isLoading,
          hasPendingOnboardingStep: Boolean(currentStep),
          errorKind:
            userProfileError instanceof Error
              ? userProfileError.constructor.name
              : typeof userProfileError,
        },
      },
    }).catch(() => {
      // Telemetry failure must not prevent access to the app.
    });
  }, [userProfileError, isOnboardingComplete, isLoading, currentStep]);

  // Send user data to analytics SDK
  const tenantId = useConfigProperty('core.tenantId');
  const identifyOptions = useMemo(
    () => ({
      employer_group_id: '',
      tenant_id: tenantId!,
    }),
    [tenantId],
  );
  useAnalyticsIdentify(identifyOptions);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!isOnboardingComplete) {
    return currentStep?.component!;
  }

  return <BasePage>{children}</BasePage>;
};
